import { get_duplicate_step_url } from '@/utils/urls.ts';
import { useState } from 'react';
import { useEditVersionContext } from '@/context/EditVersionContext/useEditVersionContext.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { notifyStepMutationError } from '@/services/stepMutationErrors.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';
import useCompositionOperations from '@/hooks/api/automations/useCompositionOperations.ts';

type duplicateStepParams = {
  step_id: string;
};

const useDuplicateStep = () => {
  const apiClient = useApiClient();
  const { addToQueue, reportLatestDraftChanged } = useEditVersionContext();
  const [pendingCount, setPendingCount] = useState(0);
  const { target } = useActiveCompositionTarget();
  const { mutate: applyComposition, isPending: isCompositionPending } = useCompositionOperations();

  const duplicateStep = (params: duplicateStepParams) => {
    if (target.scope_kind === 'local_subflow' && target.subflow_id) {
      applyComposition([
        { op_type: 'duplicate_local_subflow_step', subflow_id: target.subflow_id, step_id: params.step_id },
      ]);
      return;
    }
    setPendingCount((count) => count + 1);
    addToQueue({
      mutationFn: async (latestVersion) => {
        const response = await apiClient.put(get_duplicate_step_url(latestVersion.id), params, {
          suppressGlobalErrorToast: true,
        });
        return response.data;
      },
      onSuccess: () => {},
      onError: (error) => {
        notifyStepMutationError('duplicate step', error, reportLatestDraftChanged);
      },
      onComplete: () => {
        setPendingCount((count) => count - 1);
      },
    });
  };

  return { mutate: duplicateStep, isPending: pendingCount > 0 || isCompositionPending };
};

export default useDuplicateStep;
